// .pen: aWXWT/Hu86Z (Momento) XKJge/QG2TP (Ativos) yDv2v/sq4nz (Dívidas) G6gH8/O2vVK (Confirmação)
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Wallet } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/common/EmptyState';
import { SelfieAssetsStep } from '../../components/snapshots/ritual/SelfieAssetsStep';
import { SelfieConfirmStep } from '../../components/snapshots/ritual/SelfieConfirmStep';
import { SelfieDebtsStep } from '../../components/snapshots/ritual/SelfieDebtsStep';
import { SelfieLiveNetWorthBar } from '../../components/snapshots/ritual/SelfieLiveNetWorthBar';
import { SelfieMomentStep } from '../../components/snapshots/ritual/SelfieMomentStep';
import { WizardSegmentedProgress, WizardSkeleton, WizardStepFooter } from '../../components/wizard';
import { useActiveAccountsQuery } from '../../features/accounts/queries';
import { useLiabilitiesQuery } from '../../features/liabilities/queries';
import {
  useCreateSnapshotMutation,
  useCurrentSnapshotQuery,
  usePreviousLiabilitySnapshotEntries,
  usePreviousSnapshotEntries,
  useSelfieCyclesQuery,
} from '../../features/snapshots/queries';
import { SectionSkeleton } from '../../components/common/SectionSkeleton';
import { useQueryShellState } from '../../hooks/useQueryShellState';
import { useMobilePageHeaderTitle } from '../../store/MobilePageHeaderContext';
import pt from '../../locales/pt';
import { extractMutationError } from '../../utils/apiError';
import {
  buildPrefilledAccountEntries,
  buildPrefilledLiabilityEntries,
  buildSnapshotSubmitPayload,
  computeLiveParts,
  monthLongLabel,
} from '../../lib/selfieDisplayUtils';
import { paths } from '../../routes/paths';
import {
  getCurrentCycleFromList,
  toLocalISODate,
} from '../../utils/selfiePeriod';

const STEPS = ['intro', 'assets', 'debts', 'confirm'];

// .pen: "Selfie · Ritual · <Passo> · … " counter — «PASSO N DE 4 · <FASE>»
const STEP_PHASE_LABEL = {
  intro: 'Momento',
  assets: 'Ativos',
  debts: 'Dívidas',
  confirm: 'Confirmação',
};

function todayLocalISO() {
  return toLocalISODate();
}

function capitalizeMonth(label) {
  if (!label) return '';
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function format(template, params) {
  return template.replace(/\{(\w+)\}/g, (_, key) =>
    params[key] === undefined ? `{${key}}` : String(params[key]),
  );
}

export function SelfieNew() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const onboarding = params.get('onboarding') === '1';
  const fromGuided = params.get('from') === 'guided';
  const { data: accounts = [], isPending: accountsPending } = useActiveAccountsQuery();
  const { data: liabilities = [], isPending: liabilitiesPending } = useLiabilitiesQuery();
  const accountsShell = useQueryShellState({ data: accounts, isPending: accountsPending });
  const liabilitiesShell = useQueryShellState({ data: liabilities, isPending: liabilitiesPending });
  const isLoading = accountsShell.showBlockingSkeleton || liabilitiesShell.showBlockingSkeleton;
  const { data: cycles = [] } = useSelfieCyclesQuery();
  const { data: currentSnapshot, isPending: currentSnapshotPending } =
    useCurrentSnapshotQuery();
  const previousEntries = usePreviousSnapshotEntries();
  const previousLiabilityEntries = usePreviousLiabilitySnapshotEntries();
  const createSnapshot = useCreateSnapshotMutation();

  const activeLiabilities = useMemo(
    () => liabilities.filter((l) => !l.is_archived && !l.is_paid_off),
    [liabilities],
  );

  const currentCycle = useMemo(() => getCurrentCycleFromList(cycles), [cycles]);
  const minCycleStart = currentCycle?.minStart ?? null;
  const maxSnapshotDate = todayLocalISO();

  const [stepIndex, setStepIndex] = useState(0);
  const [direction, setDirection] = useState('forward');
  const [cycleStart, setCycleStart] = useState('');
  const [snapshotDate, setSnapshotDate] = useState('');
  const [datesInitialized, setDatesInitialized] = useState(false);
  const [accountEntries, setAccountEntries] = useState({});
  const [liabilityEntries, setLiabilityEntries] = useState({});
  const [prefillApplied, setPrefillApplied] = useState(false);

  useEffect(() => {
    if (!currentCycle || datesInitialized) return;
    const today = todayLocalISO();
    if (currentCycle.start) {
      setCycleStart(currentCycle.start);
    }
    const suggestedEnd =
      currentCycle.end && currentCycle.end <= today ? currentCycle.end : today;
    setSnapshotDate(suggestedEnd);
    setDatesInitialized(true);
  }, [currentCycle, datesInitialized]);

  useEffect(() => {
    // Wait for current/previous snapshot query — otherwise we lock empty prefills.
    if (prefillApplied || accountsShell.showBlockingSkeleton || currentSnapshotPending) {
      return;
    }
    setAccountEntries(buildPrefilledAccountEntries(accounts, previousEntries));
    setLiabilityEntries(
      buildPrefilledLiabilityEntries(activeLiabilities, previousLiabilityEntries),
    );
    setPrefillApplied(true);
  }, [
    accounts,
    activeLiabilities,
    previousEntries,
    previousLiabilityEntries,
    prefillApplied,
    accountsShell.showBlockingSkeleton,
    currentSnapshotPending,
  ]);

  const [referenceYear, referenceMonth] = useMemo(() => {
    const [year, month] = (snapshotDate || todayLocalISO()).split('-').map(Number);
    return [year, month];
  }, [snapshotDate]);

  const monthLabel = monthLongLabel(referenceMonth).toLowerCase();
  const monthTitle = capitalizeMonth(monthLabel);
  const wizardNavTitle = format(pt.snapshots.ritual.wizardTitle, {
    month: monthTitle || '…',
  });
  useMobilePageHeaderTitle(wizardNavTitle);
  const isFirstSelfie = !currentSnapshot;
  const previousNetWorth = currentSnapshot?.net_worth_eur ?? null;
  const previousReferenceMonth = currentSnapshot?.reference_month ?? null;

  const liveParts = useMemo(
    () =>
      computeLiveParts({
        accounts,
        liabilities: activeLiabilities,
        accountEntries,
        liabilityEntries,
        previousAccountEntries: previousEntries,
      }),
    [accounts, activeLiabilities, accountEntries, liabilityEntries, previousEntries],
  );

  const liveDelta =
    previousNetWorth !== null && previousNetWorth !== undefined
      ? liveParts.net - Number(previousNetWorth)
      : null;

  const canSubmit = useMemo(() => {
    const payload = buildSnapshotSubmitPayload({
      accounts,
      liabilities: activeLiabilities,
      accountEntries,
      liabilityEntries,
      referenceYear,
      referenceMonth,
      snapshotDate,
      cycleStart,
    });
    return payload.entries.length > 0 || payload.liability_entries.length > 0;
  }, [
    accounts,
    activeLiabilities,
    accountEntries,
    liabilityEntries,
    referenceYear,
    referenceMonth,
    snapshotDate,
    cycleStart,
  ]);

  const datesInvalid = Boolean(cycleStart && snapshotDate && cycleStart > snapshotDate);
  const stepKey = STEPS[stepIndex];
  const showLiveBar = stepKey === 'assets' || stepKey === 'debts';

  const goTo = (nextIndex) => {
    setDirection(nextIndex > stepIndex ? 'forward' : 'back');
    setStepIndex(nextIndex);
  };

  const handleSubmit = async () => {
    const payload = buildSnapshotSubmitPayload({
      accounts,
      liabilities: activeLiabilities,
      accountEntries,
      liabilityEntries,
      referenceYear,
      referenceMonth,
      snapshotDate,
      cycleStart,
    });
    if (!payload.entries.length && !payload.liability_entries.length) return;
    if (datesInvalid) return;

    try {
      const snapshot = await createSnapshot.mutateAsync(payload);
      const nextParams = new URLSearchParams();
      if (onboarding) nextParams.set('onboarding', '1');
      if (fromGuided) nextParams.set('from', 'guided');
      const query = nextParams.toString() ? `?${nextParams}` : '';
      navigate(`${paths.selfieResult(snapshot.id)}${query}`);
    } catch (err) {
      toast.error(extractMutationError(err, pt.snapshots.new.errorToast));
    }
  };

  const handleBack = () => {
    if (stepIndex === 0) {
      navigate(fromGuided ? `${paths.dashboard}?openGuidedFlow=1` : paths.selfies);
      return;
    }
    goTo(stepIndex - 1);
  };

  const handlePrimary = () => {
    if (stepKey === 'confirm') {
      handleSubmit();
      return;
    }
    goTo(Math.min(stepIndex + 1, STEPS.length - 1));
  };

  const footerLabel =
    stepKey === 'intro'
      ? isFirstSelfie
        ? pt.snapshots.ritual.moment.ctaFirst
        : pt.snapshots.ritual.moment.cta
      : stepKey === 'confirm'
        ? pt.snapshots.ritual.confirm.cta
        : pt.snapshots.ritual.balances.cta;

  if (isLoading) {
    return (
      <WizardSkeleton
        appChrome
        wizardTitle={wizardNavTitle}
        step={1}
        total={STEPS.length}
        showProgress
        showPercent={false}
        loading
      >
        <SectionSkeleton variant="list" className="mx-auto max-w-lg" />
      </WizardSkeleton>
    );
  }

  if (accounts.length === 0 && activeLiabilities.length === 0) {
    return (
      <EmptyState
        icon={Wallet}
        title={pt.snapshots.new.empty}
        action={
          <div className="flex flex-wrap justify-center gap-2">
            <Button onClick={() => navigate(paths.ativos)}>
              {pt.snapshots.new.goToAccounts}
            </Button>
            <Button variant="secondary" onClick={() => navigate(paths.dividas)}>
              {pt.liabilities.addDebt}
            </Button>
          </div>
        }
      />
    );
  }

  return (
    <WizardSkeleton
      appChrome
      wizardTitle={wizardNavTitle}
      step={stepIndex + 1}
      total={STEPS.length}
      onBack={handleBack}
      showBack
      direction={direction}
      loading={createSnapshot.isPending}
      showProgress
      progress={
        <WizardSegmentedProgress
          step={stepIndex + 1}
          total={STEPS.length}
          phaseLabel={STEP_PHASE_LABEL[stepKey]}
        />
      }
      footer={
        <div className="flex flex-col gap-space-3">
          {showLiveBar ? (
            <SelfieLiveNetWorthBar
              total={liveParts.net}
              delta={liveDelta}
              previousMonthLabel={
                previousReferenceMonth ? monthLongLabel(previousReferenceMonth) : null
              }
              hasPrevious={previousNetWorth !== null && previousNetWorth !== undefined}
            />
          ) : null}
          <WizardStepFooter
            primaryLabel={footerLabel}
            onPrimary={handlePrimary}
            onBack={stepKey === 'intro' ? undefined : handleBack}
            loading={createSnapshot.isPending}
            disabled={stepKey === 'confirm' && (!canSubmit || datesInvalid)}
          />
        </div>
      }
    >
      {stepKey === 'intro' ? (
        <SelfieMomentStep
          monthLabel={monthLabel}
          isFirstSelfie={isFirstSelfie}
          assetCount={accounts.length}
          debtCount={activeLiabilities.length}
        />
      ) : null}

      {stepKey === 'assets' ? (
        <SelfieAssetsStep
          accounts={accounts}
          accountEntries={accountEntries}
          previousAccountEntries={previousEntries}
          onAccountEntriesChange={setAccountEntries}
          isFirstSelfie={isFirstSelfie}
        />
      ) : null}

      {stepKey === 'debts' ? (
        <SelfieDebtsStep
          liabilities={activeLiabilities}
          liabilityEntries={liabilityEntries}
          previousLiabilityEntries={previousLiabilityEntries}
          onLiabilityEntriesChange={setLiabilityEntries}
        />
      ) : null}

      {stepKey === 'confirm' ? (
        <SelfieConfirmStep
          assetsSum={liveParts.assets}
          debtsSum={liveParts.debt}
          netWorth={liveParts.net}
          showDateFields
          cycleStart={cycleStart}
          onCycleStartChange={setCycleStart}
          minCycleStart={minCycleStart}
          snapshotDate={snapshotDate}
          onSnapshotDateChange={setSnapshotDate}
          maxSnapshotDate={maxSnapshotDate}
        />
      ) : null}
    </WizardSkeleton>
  );
}

export default SelfieNew;
